import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, MenuItem, Grid, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Box,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { PricingModelResponse, CreatePricingModelRequest } from '../../../app/services/api/ofdClient';
import useNswagClient from '../../../hooks/api/useNswagClient';

interface TierRow {
  rowId: string;
  fromQuantity: string;
  toQuantity: string;
  rate: string;
  sortOrder: number;
}

interface Props {
  model: PricingModelResponse | null;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}

const MODEL_TYPES = [
  { value: 1, labelKey: 'modelTypeFixed', fallback: 'Fixed' },
  { value: 2, labelKey: 'modelTypeBreak', fallback: 'Break' },
  { value: 3, labelKey: 'modelTypeCumulative', fallback: 'Cumulative' },
];

const RATE_TYPES = [
  { value: 1, labelKey: 'rateTypeFixedPrice', fallback: 'Fixed Price' },
  { value: 2, labelKey: 'rateTypeDiscountPercent', fallback: 'Discount %' },
];

const BILLING_MODES = [
  { value: 1, labelKey: 'billingModePartialOnly', fallback: 'Partial Delivery Units Only' },
  { value: 2, labelKey: 'billingModeFullOnly', fallback: 'Full Delivery Units Only' },
  { value: 3, labelKey: 'billingModeBoth', fallback: 'Full + Partial Delivery Units' },
];

const FIXED_MODEL_TYPE = 1;
const DISCOUNT_RATE_TYPE = 2;
const LINEAR_STRATEGY = 1;
const FULL_PALLETS_ONLY_BILLING_MODE = 2;

const makeTierRowId = (counter: number) => `tier-${counter}`;

const toNumberOrNull = (value: string): number | null => {
  if (!value.trim()) {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toQuantityInputValue = (value?: number | string | null): string => {
  if (value == null) {
    return '';
  }

  const normalized = value.toString().trim().toLowerCase();
  if (!normalized || normalized === 'inf' || normalized === 'infinity' || normalized === '∞') {
    return '';
  }

  return value.toString();
};

const PricingModelEditor: React.FC<Props> = ({ model, open, onClose, onSaved }) => {
  const isEdit = !!model;
  const { t } = useTranslation('deliveryPricing');
  const { pricingModelsClient } = useNswagClient();

  const [name, setName] = useState('');
  const [modelType, setModelType] = useState(2);
  const [rateType, setRateType] = useState(1);
  const [partialQuantityStrategy, setPartialQuantityStrategy] = useState(1);
  const [roundUpThresholdPercent, setRoundUpThresholdPercent] = useState('');
  const [billingMode, setBillingMode] = useState(1);
  const [tiers, setTiers] = useState<TierRow[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [touchedTierRows, setTouchedTierRows] = useState<Set<string>>(new Set());
  const tierIdCounterRef = useRef(1);

  const isFixedModel = modelType === FIXED_MODEL_TYPE;
  const isDiscountRate = rateType === DISCOUNT_RATE_TYPE;
  const showTierActions = !isFixedModel;
  const helperTextProps = { sx: { ml: 0 } };
  const rateColumnLabel = isDiscountRate
    ? t('modelEditor.discountPercent', 'Discount %')
    : t('modelEditor.ratePerPallet', 'Rate per delivery unit');
  const toQuantityPlaceholder = t('modelEditor.toQuantityOpenEndedPlaceholder', 'No upper limit');

  const isTierRowValid = (tier: TierRow): boolean => {
    const from = toNumberOrNull(tier.fromQuantity);
    const to = toNumberOrNull(tier.toQuantity);
    const rate = toNumberOrNull(tier.rate);

    if (from === null || from <= 0) {
      return false;
    }

    if (to !== null && to < from) {
      return false;
    }

    if (rate === null || rate < 0) {
      return false;
    }

    if (isDiscountRate && rate > 100) {
      return false;
    }

    return true;
  };

  const getTierValidationError = (currentTiers: TierRow[]): string | null => {
    if (currentTiers.length === 0) {
      return t('modelEditor.tierRequired', 'At least one tier is required');
    }

    const ordered = [...currentTiers].sort((a, b) => a.sortOrder - b.sortOrder);

    if (isFixedModel) {
      const fixed = ordered[0];
      if (ordered.length !== 1 || fixed.fromQuantity !== '1' || !!fixed.toQuantity) {
        return t('modelEditor.fixedTierInvalid', 'Fixed model must have a single tier from 1 to infinity');
      }
    }

    if (toNumberOrNull(ordered[0].fromQuantity) !== 1) {
      return t('modelEditor.tierFirstFromOne', 'First tier must start from quantity 1');
    }

    for (let index = 0; index < ordered.length; index++) {
      const tier = ordered[index];
      const from = toNumberOrNull(tier.fromQuantity);

      if (!isTierRowValid(tier)) {
        return t('modelEditor.tierInvalid', 'Please enter valid tier ranges and rates');
      }

      if (from === null) {
        return t('modelEditor.tierInvalid', 'Please enter valid tier ranges and rates');
      }

      if (index === 0) {
        continue;
      }

      const previous = ordered[index - 1];
      const previousTo = toNumberOrNull(previous.toQuantity);
      if (previousTo === null) {
        return t('modelEditor.tierOpenEndedMustBeLast', 'Open-ended tier must be last');
      }

      if (from <= previousTo) {
        return t('modelEditor.tierOverlap', 'Tiers cannot overlap');
      }

      if (from > previousTo + 1) {
        return t('modelEditor.tierGap', 'Tiers cannot have gaps');
      }
    }

    if (!isFixedModel) {
      const lastTier = ordered[ordered.length - 1];
      if (toNumberOrNull(lastTier.toQuantity) !== null) {
        return t('modelEditor.tierLastMustBeOpenEnded', 'The last tier must have no upper limit (leave To Quantity blank)');
      }
    }

    return null;
  };

  const tierValidationError = getTierValidationError(tiers);
  const canSave =
    name.trim().length > 0
    && tiers.length > 0
    && (!roundUpThresholdPercent || (Number.isFinite(Number(roundUpThresholdPercent)) && Number(roundUpThresholdPercent) >= 0 && Number(roundUpThresholdPercent) <= 100))
    && !tierValidationError;

  useEffect(() => {
    if (model) {
      setName(model.name ?? '');
      setModelType(model.modelType ?? 2);
      setRateType(model.rateType ?? 1);
      setPartialQuantityStrategy(model.partialQuantityStrategy ?? 1);
      setRoundUpThresholdPercent(model.roundUpThresholdPercent?.toString() ?? '');
      setBillingMode(model.billingMode ?? 1);
      setTiers(
        (model.tiers ?? []).map((t, i) => ({
          rowId: makeTierRowId(tierIdCounterRef.current++),
          fromQuantity: (t.fromQuantity ?? 0).toString(),
          toQuantity: toQuantityInputValue(t.toQuantity),
          rate: (t.rate ?? 0).toString(),
          sortOrder: t.sortOrder || i + 1,
        })),
      );
    } else {
      setName('');
      setModelType(2);
      setRateType(1);
      setPartialQuantityStrategy(1);
      setRoundUpThresholdPercent('');
      setBillingMode(1);
      setTiers([{ rowId: makeTierRowId(tierIdCounterRef.current++), fromQuantity: '1', toQuantity: '', rate: '', sortOrder: 1 }]);
    }

    setSubmitAttempted(false);
    setTouchedTierRows(new Set());
  }, [model, open]);

  useEffect(() => {
    // Delivery pricing currently supports linear partial strategy only.
    if (partialQuantityStrategy !== LINEAR_STRATEGY) {
      setPartialQuantityStrategy(LINEAR_STRATEGY);
    }
  }, [partialQuantityStrategy]);

  useEffect(() => {
    if (!isFixedModel) {
      return;
    }

    setTiers(prev => {
      const first = prev[0] ?? { rowId: makeTierRowId(tierIdCounterRef.current++), fromQuantity: '1', toQuantity: '', rate: '', sortOrder: 1 };
      return [{ ...first, fromQuantity: '1', toQuantity: '', sortOrder: 1 }];
    });
  }, [isFixedModel]);

  const addTier = () => {
    setTiers(prev => {
      const lastTier = prev[prev.length - 1];
      const nextFrom = lastTier?.toQuantity
        ? (parseFloat(lastTier.toQuantity) + 1).toString()
        : '';

      return [
        ...prev,
        {
          rowId: makeTierRowId(tierIdCounterRef.current++),
          fromQuantity: nextFrom,
          toQuantity: '',
          rate: '',
          sortOrder: prev.length + 1,
        },
      ];
    });
  };

  const removeTier = (idx: number) => {
    const removedRowId = tiers[idx]?.rowId;
    setTiers(prev => prev.filter((_, i) => i !== idx).map((t, i) => ({ ...t, sortOrder: i + 1 })));
    if (removedRowId) {
      setTouchedTierRows(prev => {
        const next = new Set(prev);
        next.delete(removedRowId);
        return next;
      });
    }
  };

  const moveTier = (idx: number, direction: -1 | 1) => {
    setTiers(prev => {
      const newTiers = [...prev];
      const swapIdx = idx + direction;
      if (swapIdx < 0 || swapIdx >= newTiers.length) {
        return prev;
      }

      [newTiers[idx], newTiers[swapIdx]] = [newTiers[swapIdx], newTiers[idx]];
      return newTiers.map((t, i) => ({ ...t, sortOrder: i + 1 }));
    });
  };

  const updateTier = (idx: number, field: keyof TierRow, value: string) => {
    const rowId = tiers[idx]?.rowId;
    if (rowId) {
      setTouchedTierRows(prev => {
        const next = new Set(prev);
        next.add(rowId);
        return next;
      });
    }

    setTiers(prev => prev.map((t, i) => (i === idx ? { ...t, [field]: value } : t)));
  };

  const handleSave = async () => {
    setError('');
    setSubmitAttempted(true);
    if (!name.trim()) { setError(t('modelEditor.nameRequired', 'Name is required')); return; }
    if (tiers.length === 0) { setError(t('modelEditor.tierRequired', 'At least one tier is required')); return; }
    if (tiers.some(t => !t.rate)) { setError(t('modelEditor.tierRateRequired', 'All tiers must have a rate')); return; }
    if (roundUpThresholdPercent && (!Number.isFinite(Number(roundUpThresholdPercent)) || Number(roundUpThresholdPercent) < 0 || Number(roundUpThresholdPercent) > 100)) {
      setError(t('modelEditor.roundUpThresholdInvalid', 'Round up threshold must be between 0 and 100'));
      return;
    }
    if (tierValidationError) {
      setError(tierValidationError);
      return;
    }

    const request: CreatePricingModelRequest = {
      name: name.trim(),
      modelType: modelType as CreatePricingModelRequest['modelType'],
      rateType: rateType as CreatePricingModelRequest['rateType'],
      partialQuantityStrategy: partialQuantityStrategy as CreatePricingModelRequest['partialQuantityStrategy'],
      roundUpThresholdPercent: roundUpThresholdPercent ? parseFloat(roundUpThresholdPercent) : undefined,
      billingMode: billingMode as CreatePricingModelRequest['billingMode'],
      tiers: tiers.map(t => ({
        fromQuantity: parseFloat(t.fromQuantity) || 0,
        toQuantity: t.toQuantity ? parseFloat(t.toQuantity) : undefined,
        rate: parseFloat(t.rate) || 0,
        sortOrder: t.sortOrder,
      })),
    };

    setSaving(true);
    try {
      if (isEdit) {
        await pricingModelsClient.pricingModelsPUT(model!.pricingModelId!, request);
      } else {
        await pricingModelsClient.pricingModelsPOST(request);
      }
      onSaved();
    } catch {
      setError(t('modelEditor.saveFailed', 'Failed to save pricing model'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth>
      <DialogTitle>{isEdit ? t('modelEditor.editTitle', 'Edit Pricing Model') : t('modelEditor.createTitle', 'Create Pricing Model')}</DialogTitle>
      <DialogContent>
        <Grid
          container
          spacing={2}
          sx={{ mt: 1 }}>
          <Grid
            item
            xs={12}>
            <TextField
              fullWidth
              required
              label={t('name', 'Name')}
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder={t('modelEditor.namePlaceholder', 'e.g. Standard UK Delivery')}
            />
          </Grid>
          <Grid
            item
            xs={6}>
            <TextField
              select
              fullWidth
              label={t('modelType', 'Model Type')}
              value={modelType}
              onChange={e => setModelType(Number(e.target.value))}
            >
              {MODEL_TYPES.map(mt => (
                <MenuItem
                  key={mt.value}
                  value={mt.value}>{t(mt.labelKey, mt.fallback)}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid
            item
            xs={6}>
            <TextField
              select
              fullWidth
              label={t('rateType', 'Rate Type')}
              value={rateType}
              onChange={e => setRateType(Number(e.target.value))}
            >
              {RATE_TYPES.map(rt => (
                <MenuItem
                  key={rt.value}
                  value={rt.value}>{t(rt.labelKey, rt.fallback)}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid
            item
            xs={12}>
            <TextField
              select
              fullWidth
              label={t('modelEditor.billingMode', 'Billing Mode')}
              value={billingMode}
              onChange={e => setBillingMode(Number(e.target.value))}
              helperText={t('modelEditor.billingModeHelp', 'Which portion of the order is charged for delivery.')}
              FormHelperTextProps={helperTextProps}
            >
              {BILLING_MODES.map(bm => (
                <MenuItem
                  key={bm.value}
                  value={bm.value}>{t(bm.labelKey, bm.fallback)}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid
            item
            xs={12}>
            <TextField
              fullWidth
              type="number"
              label={t('modelEditor.roundUpThreshold', 'Round Up Threshold %')}
              value={roundUpThresholdPercent}
              onChange={e => setRoundUpThresholdPercent(e.target.value)}
              helperText={billingMode === FULL_PALLETS_ONLY_BILLING_MODE
                ? t('modelEditor.roundUpThresholdIgnoredHelp', 'Not used — Full Delivery Units Only does not apply a partial-unit threshold.')
                : t('modelEditor.roundUpThresholdHelp', 'If fill ≥ this %, round up to full delivery unit. Range: 0-100.')}
              FormHelperTextProps={helperTextProps}
              placeholder={t('modelEditor.roundUpThresholdPlaceholder', 'e.g. 90')}
              inputProps={{ min: 0, max: 100, step: 1 }}
            />
          </Grid>

          <Grid
            item
            xs={12}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={1}>
              <Typography>{t('modelEditor.tierBands', 'Tier Bands')}</Typography>
              {!isFixedModel && (
                <Button
                  startIcon={<AddIcon />}
                  onClick={addTier}>
                  {t('modelEditor.addTier', 'Add Tier')}
                </Button>
              )}
            </Box>
            <Typography
              variant="textXS"
              color="text.secondary"
              sx={{ mb: 1 }}>
              {isFixedModel
                ? t('modelEditor.fixedTierRulesHelp', 'Fixed model uses one tier only: from 1 with no upper limit.')
                : t(
                  'modelEditor.tierRulesHelp',
                  'Tiers must start at 1, remain contiguous with no overlaps or gaps, and the last tier must have no upper limit. Use arrows to reorder where needed.',
                )}
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width="5%">#</TableCell>
                    <TableCell width="25%">{t('modelEditor.fromQuantity', 'From Quantity')}</TableCell>
                    <TableCell width="25%">{t('modelEditor.toQuantity', 'To Quantity')}</TableCell>
                    <TableCell width={showTierActions ? '25%' : '45%'}>{rateColumnLabel}</TableCell>
                    {showTierActions && (
                      <TableCell
                        width="140px"
                        align="right">{t('actions', 'Actions')}</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tiers.map((tier, idx) => (
                    <TableRow key={tier.rowId}>
                      <TableCell>{tier.sortOrder}</TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={tier.fromQuantity}
                          onChange={e => updateTier(idx, 'fromQuantity', e.target.value)}
                          inputProps={{ min: 1, step: 0.01 }}
                          placeholder={t('modelEditor.fromQuantityPlaceholder', 'e.g. 1')}
                          fullWidth
                          disabled={isFixedModel}
                        />
                      </TableCell>
                      <TableCell>
                        {isFixedModel ? (
                          <TextField
                            size="small"
                            type="text"
                            value={t('modelEditor.infinityText', 'No upper limit')}
                            fullWidth
                            disabled
                          />
                        ) : (
                          <TextField
                            size="small"
                            type="number"
                            value={tier.toQuantity}
                            onChange={e => updateTier(idx, 'toQuantity', e.target.value)}
                            placeholder={toQuantityPlaceholder}
                            inputProps={{ min: 0, step: 0.01 }}
                            fullWidth
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={tier.rate}
                          onChange={e => updateTier(idx, 'rate', e.target.value)}
                          inputProps={{
                            min: 0,
                            max: isDiscountRate ? 100 : undefined,
                            step: 0.01,
                          }}
                          placeholder={isDiscountRate
                            ? t('modelEditor.discountRatePlaceholder', 'e.g. 10')
                            : t('modelEditor.fixedRatePlaceholder', 'e.g. 66.85')}
                          fullWidth
                          required
                          error={(submitAttempted || touchedTierRows.has(tier.rowId)) && !isTierRowValid(tier)}
                        />
                      </TableCell>
                      {showTierActions && (
                        <TableCell
                          align="right"
                          sx={{ whiteSpace: 'nowrap' }}>
                          <Box
                            display="flex"
                            justifyContent="flex-end"
                            alignItems="center"
                            gap={0.5}
                            sx={{ minHeight: 32 }}>
                            <IconButton
                              size="small"
                              onClick={() => moveTier(idx, -1)}
                              disabled={idx === 0}
                              sx={{ color: 'text.primary' }}>
                              <ArrowUpwardIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => moveTier(idx, 1)}
                              disabled={idx === tiers.length - 1}
                              sx={{ color: 'text.primary' }}>
                              <ArrowDownwardIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => removeTier(idx)}
                              disabled={tiers.length <= 1}
                              sx={{ color: 'error.main' }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          {error && (
            <Grid
              item
              xs={12}>
              <Typography
                color="error">{error}</Typography>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('cancel', 'Cancel')}</Button>
        <Button
          onClick={handleSave}
          disabled={saving || !canSave}>
          {saving ? t('modelEditor.saving', 'Saving...') : t('ruleEditor.save', 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PricingModelEditor;
